import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spinner, Alert, Container } from 'react-bootstrap';
import { useProfileCompletion } from '../hooks/useProfileCompletion';
import { useAuth } from '../context/AuthContext';

/**
 * Component that guards routes requiring complete profile
 * Redirects to profile completion page if profile is incomplete
 */
const ProfileGuard = ({ children, requireComplete = true }) => {
  const { user, isAuthenticated } = useAuth();
  const { 
    completionStatus, 
    loading, 
    checkingProfile, 
    isComplete,
    isRouteExempt 
  } = useProfileCompletion();
  const location = useLocation();

  // Don't guard if user is not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Don't guard exempt routes
  if (isRouteExempt(location.pathname)) {
    return children;
  }

  // Show loading while checking profile
  if (loading || checkingProfile) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <Spinner animation="border" role="status" className="mb-3">
            <span className="visually-hidden">Đang kiểm tra thông tin cá nhân...</span>
          </Spinner>
          <p>Đang kiểm tra thông tin cá nhân...</p>
        </div>
      </Container>
    );
  }

  // If profile completion is required but not complete, redirect
  if (requireComplete && !isComplete) {
    return (
      <Navigate 
        to="/complete-profile" 
        replace 
        state={{ from: location.pathname }}
      />
    );
  }

  // Profile is complete or completion not required, render children
  return children;
};

/**
 * Higher-order component to wrap routes that require complete profile
 */
export const withProfileGuard = (Component, options = {}) => {
  const { requireComplete = true } = options;
  
  return function ProfileGuardedComponent(props) {
    return (
      <ProfileGuard requireComplete={requireComplete}>
        <Component {...props} />
      </ProfileGuard>
    );
  };
};

/**
 * Component to show profile completion status banner
 */
export const ProfileCompletionBanner = () => {
  const { user } = useAuth();
  const location = useLocation();

  // Don't show banner on profile completion page
  if (location.pathname === '/complete-profile') {
    return null;
  }

  // Don't show if profile is complete (check from user context, not hook)
  if (user?.profileCompletion?.isComplete) {
    return null;
  }

  // Don't show if no user or no completion status yet
  if (!user || !user.profileCompletion) {
    return null;
  }

  const completionStatus = user.profileCompletion;

  return (
    <Alert variant="warning" className="mb-0 rounded-0">
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <Alert.Heading className="h6 mb-1">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Thông tin cá nhân chưa đầy đủ
          </Alert.Heading>
          <p className="mb-0">
            Hoàn thành: {completionStatus.completionPercentage}%
            ({completionStatus.completedFields?.length || 0}/{completionStatus.requiredFields?.length || 5} trường bắt buộc)
          </p>
        </div>
        <div>
          <a href="/complete-profile" className="btn btn-warning btn-sm">
            <i className="fas fa-edit me-1"></i>
            Hoàn thành ngay
          </a>
        </div>
      </div>
    </Alert>
  );
};

export default ProfileGuard;
